import { MedicalItemMedicationAndPrescription } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescription";
import { MedicationNote } from "@/Patient/Models/MedicalSummary/MedicationNote";

/**
 * Service that will manage medication notes.
 */
class MedicationNoteService {
    // The key is the medication id and the array is the list of medication note ids.
    private readonly noteMapper: { [index: string]: string[] } = {};

    // The key is the medication id (medicalItemId) and the array is the list of Medication Notes associated
    private medicationNotesByMedicationId: { [medicationId: string]: MedicationNote[] } = {};
    // The key is the medication id (medicalItemId) and the array is the list of current medication note ids
    private medicationNoteIdsByMedicationIds: { [medicationId: string]: Set<string> } = {};

    // List of current medications with medication id (medicalItemId) as the key
    private readonly medications: { [index: string]: MedicalItemMedicationAndPrescription } = {};

    private medicationHiearchy: { [medicationId: string]: Set<string> } = {};

    public constructor(noteMapper: { [index: string]: string[] }, medications: MedicalItemMedicationAndPrescription[]) {
        this.noteMapper = noteMapper;
        this.medications = medications.toDictionary(p => p.MedicalItemId);
        this.init();
    }

    /**
     * Get the list of current medication notes for the given medication id provided.
     * @param medicationId
     * @returns Lsit of the medication note models
     */
    public getNotesByMedicationId(medicationId: string): MedicationNote[] {
        return this.medicationNotesByMedicationId[medicationId] ?? [];
    }

    /**
     * Load the medication note models received from backend.
     * @param medicationNotes The medication notes to add to the dictionary
     * @param medicationId The id of the medication to add the notes too.
     * @returns Current instance of MedicationNoteService
     */
    public loadMedicationNotes(medicationNotes: MedicationNote[], medicationId: string): this {
        for (let note of medicationNotes) {
            if (this.medicationNoteIdsByMedicationIds[medicationId]?.has(note.Id)) {
                let existingNote = this.medicationNotesByMedicationId[medicationId].find(p => p.Id === note.Id);
                existingNote.MedicationId = medicationId;
                existingNote.EmployeeId = note.EmployeeId;
                existingNote.Note = note.Note;
                existingNote.CreationDate = note.CreationDate;
                existingNote.IsLoaded = true;
            }
        }

        return this;
    }

    /**
     * Adds a currently added note the service.
     * @param medicationId The id of the medication we want to add the note too
     * @param note The Medication note
     * @returns Current instance of MedicationNoteService
     */
    public addMedicationNote(medicationId: string, note: MedicationNote): this {
        const medicationIds = [medicationId, ...Array.from(this.medicationHiearchy[medicationId])];

        for (let id of Array.from(medicationIds)) {
            if (this.noteMapper[id] !== undefined)
                this.noteMapper[id].push(note.Id);

            if (this.medicationNoteIdsByMedicationIds[id] !== undefined)
                this.medicationNoteIdsByMedicationIds[id].add(note.Id);

            if (this.medicationNotesByMedicationId[id] !== undefined)
                this.medicationNotesByMedicationId[id].push(new MedicationNote({ ...note, IsLoaded: true }));
        }

        return this;
    }

    private init(): void {
        const visited: Set<string> = new Set();
        for (let med of Object.values<MedicalItemMedicationAndPrescription>(this.medications)) {
            this.medicationNotesByMedicationId[med.MedicalItemId] = this.fillMedicationNotes(med, [], visited);
            this.medicationNoteIdsByMedicationIds[med.MedicalItemId] = new Set(this.medicationNotesByMedicationId[med.MedicalItemId].map(p => p.Id));
            visited.clear();
        }
        this.buildMedicationDendencyTree();
    }

    private buildMedicationDendencyTree(): void {
        this.medicationHiearchy = this.buildMedicationTree(Object.values<MedicalItemMedicationAndPrescription>(this.medications));
    }

    private buildMedicationTree(medicationList: MedicalItemMedicationAndPrescription[]): Record<string, Set<string>> {
        // Step 1: Initialize the result object where each medication ID will map to a Set of its descendants
        const tree: Record<string, Set<string>> = {};

        // Step 2: Create a function to populate all descendants
        function getAllDescendants(medication: MedicalItemMedicationAndPrescription): Set<string> {
            const descendants: Set<string> = new Set();

            // Find all direct children
            for (const otherMedication of medicationList) {
                // MedicalItemId should never be null or undefined but 1 edge case causes this.
                // If the value is Guid.Empty, then the value will never be sent to the front end. The actual raw value needs to be fixed in the DB.
                if (medication.MedicalItemId && otherMedication.OriginalMedicationId === medication.MedicalItemId) {
                    // Add the child medication
                    descendants.add(otherMedication.MedicalItemId);

                    // Recursively get all descendants of this child
                    const childDescendants = getAllDescendants(otherMedication);
                    for (const descendant of Array.from(childDescendants)) {
                        descendants.add(descendant);
                    }
                }
            }

            return descendants;
        }

        // Step 3: Iterate over each medication in the list
        for (const medication of medicationList) {
            // Populate the tree for each medication
            tree[medication.MedicalItemId] = getAllDescendants(medication);
        }

        // Step 4: Return the populated tree object
        return tree;
    }

    private fillMedicationNotes(medication: MedicalItemMedicationAndPrescription, notes: MedicationNote[], visited: Set<string>): MedicationNote[] {
        if (visited.has(medication.MedicalItemId))
            return;

        visited.add(medication.MedicalItemId);

        const newNotes = this.getMedicationNotesById(medication.MedicalItemId)
            .map(p => new MedicationNote({ Id: p }));

        notes.push(...newNotes);

        if (medication.OriginalMedicationId && this.medications[medication.OriginalMedicationId])
            this.fillMedicationNotes(this.medications[medication.OriginalMedicationId], notes, visited);

        return notes.distinctByProperty(p => p.Id);
    }

    private getMedicationNotesById(id: string): string[] {
        return this.noteMapper?.[id] ?? [];
    }
}

export { MedicationNoteService };
